import React, {useState, useEffect} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import './ReferencesManagement.css';

const API_URL = import.meta.env.VITE_API_URL;

const ReferencesManagement = ({onNavigate, onLogout, currentPage}) => {
    const {hasPermission} = usePermissions();
    const [references, setReferences] = useState([]);
    const [filtreType, setFiltreType] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingReference, setEditingReference] = useState(null);
    const [formData, setFormData] = useState({
        nom: '',
        typeReference: '',
        puissance: '',
        logo: null
    });

    const typesReference = ['MT', 'BT', 'Pompage', 'Isolé'];

    useEffect(() => {
        fetchReferences();
    }, [filtreType]);

    const fetchReferences = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let url = `${API_URL}/references`;
            if (filtreType) url += `?typeReference=${filtreType}`;

            const response = await axios.get(url, {
                headers: {Authorization: `Bearer ${token}`}
            });
            setReferences(response.data);
        } catch (error) {
            console.error('Erreur:', error);
            Swal.fire('Erreur', 'Impossible de charger les références', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
    };

    const handleLogoChange = (e) => {
        setFormData({...formData, logo: e.target.files[0]});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const data = new FormData();
        data.append('nom', formData.nom);
        data.append('typeReference', formData.typeReference);
        data.append('puissance', formData.puissance);

        if (formData.logo) {
            data.append('logo', formData.logo);
        }

        try {
            const token = localStorage.getItem('token');
            if (editingReference) {
                await axios.put(`${API_URL}/references/${editingReference._id}`,
                    data,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                Swal.fire('Succès', 'Référence mise à jour', 'success');
            } else {
                await axios.post(`${API_URL}/references`,
                    data,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                Swal.fire('Succès', 'Référence ajoutée', 'success');
            }
            setShowForm(false);
            resetForm();
            fetchReferences();
        } catch (error) {
            Swal.fire('Erreur', error.response?.data?.message || 'Erreur serveur', 'error');
        }
    };

    const handleEdit = (reference) => {
        setEditingReference(reference);
        setFormData({
            nom: reference.nom,
            typeReference: reference.typeReference,
            puissance: reference.puissance,
            logo: null
        });
        setShowForm(true);
    };

    const handleDelete = async (id, nom) => {
        const result = await Swal.fire({
            title: 'Supprimer cette référence ?',
            html: `Êtes-vous sûr de vouloir supprimer <strong>${nom}</strong> ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonText: 'Annuler',
            confirmButtonText: 'Supprimer'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/references/${id}`, {
                    headers: {Authorization: `Bearer ${token}`}
                });
                Swal.fire('Supprimé', 'Référence supprimée avec succès', 'success');
                fetchReferences();
            } catch (error) {
                Swal.fire('Erreur', error.response?.data?.message || 'Erreur serveur', 'error');
            }
        }
    };

    const resetForm = () => {
        setFormData({
            nom: '',
            typeReference: '',
            puissance: '',
            logo: null
        });
        setEditingReference(null);
    };

    const getTypeColor = (type) => {
        const colors = {
            'MT': '#ff6b6b',
            'BT': '#4ecdc4',
            'Pompage': '#45b7d1',
            'Isolé': '#f9ca24'
        };
        return colors[type] || '#666';
    };

    const countByType = (type) => {
        return references.filter(ref => ref.typeReference === type).length;
    };

    return (
        <div className="backoffice-layout">
            <BackOfficeMenu
                onNavigate={onNavigate}
                onLogout={onLogout}
                currentPage={currentPage}
            />
            <div className="backoffice-content">
                <div className="references-management">
                    <div className="dashboard-header">
                        <div>
                            <h1>Gestion des Références</h1>
                            <p>Gestion des projets réalisés</p>
                        </div>
                        {hasPermission('references', 'create') && (
                            <button
                                className="btn-add-main"
                                onClick={() => {
                                    setShowForm(true);
                                    resetForm();
                                }}
                            >
                                ➕ Ajouter une référence
                            </button>
                        )}
                    </div>

                    {/* Statistiques par type */}
                    <div className="stats-grid">
                        {typesReference.map(type => (
                            <div
                                key={type}
                                className="stat-card"
                                style={{borderLeftColor: getTypeColor(type)}}
                            >
                                <div className="stat-icon" style={{background: getTypeColor(type)}}>
                                    {type === 'MT' && '⚡'}
                                    {type === 'BT' && '🔌'}
                                    {type === 'Pompage' && '💧'}
                                    {type === 'Isolé' && '🏝️'}
                                </div>
                                <div className="stat-info">
                                    <h3>{type}</h3>
                                    <p className="stat-number">{countByType(type)}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Filtres */}
                    <div className="filters-bar">
                        <div className="filter-group">
                            <label>Filtrer par type :</label>
                            <select
                                value={filtreType}
                                onChange={(e) => setFiltreType(e.target.value)}
                            >
                                <option value="">Tous les types</option>
                                {typesReference.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        {filtreType && (
                            <button
                                className="btn-reset-filter"
                                onClick={() => setFiltreType('')}
                            >
                                ✕ Réinitialiser
                            </button>
                        )}
                    </div>

                    {/* Formulaire */}
                    {showForm && (
                        <div className="form-overlay" onClick={() => setShowForm(false)}>
                            <div className="form-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="form-header">
                                    <h2>{editingReference ? 'Modifier la référence' : 'Nouvelle référence'}</h2>
                                    <button
                                        className="close-btn"
                                        onClick={() => setShowForm(false)}
                                    >
                                        ✕
                                    </button>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label>Nom de la référence *</label>
                                        <input
                                            type="text"
                                            name="nom"
                                            value={formData.nom}
                                            onChange={handleInputChange}
                                            required
                                            placeholder="Ex: Centrale solaire Ouarzazate"
                                        />
                                    </div>

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>Type *</label>
                                            <select
                                                name="typeReference"
                                                value={formData.typeReference}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Sélectionner...</option>
                                                {typesReference.map(type => (
                                                    <option key={type} value={type}>{type}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label>Puissance *</label>
                                            <input
                                                type="text"
                                                name="puissance"
                                                value={formData.puissance}
                                                onChange={handleInputChange}
                                                required
                                                placeholder="Ex: 500 kW"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Logo {!editingReference && '*'}</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoChange}
                                            required={!editingReference}
                                        />
                                        {editingReference && (
                                            <small className="form-hint">
                                                Laisser vide pour conserver le logo actuel
                                            </small>
                                        )}
                                    </div>

                                    <div className="form-actions">
                                        <button type="submit" className="btn-submit">
                                            {editingReference ? '💾 Mettre à jour' : '➕ Ajouter'}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-cancel"
                                            onClick={() => setShowForm(false)}
                                        >
                                            Annuler
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* Liste des références */}
                    {loading ? (
                        <LoadingSpinner message="Chargement des références..." />
                    ) : references.length === 0 ? (
                        <div className="empty-state">
                            <p>Aucune référence trouvée</p>
                            {filtreType && <small>Essayez de modifier le filtre</small>}
                        </div>
                    ) : (
                        <div className="references-grid">
                            {references.map(reference => (
                                <div key={reference._id} className="reference-card">
                                    <div className="card-logo">
                                        <img
                                            src={`${API_URL.replace('/api', '')}${reference.logo}`}
                                            alt={reference.nom}
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/200x100?text=Logo';
                                            }}
                                        />
                                        <div
                                            className="type-badge"
                                            style={{background: getTypeColor(reference.typeReference)}}
                                        >
                                            {reference.typeReference}
                                        </div>
                                    </div>
                                    <div className="card-content">
                                        <h3>{reference.nom}</h3>
                                        <div className="card-info">
                                            <span className="info-item">
                                                <strong>⚡</strong> {reference.puissance}
                                            </span>
                                        </div>
                                        <div className="card-actions">
                                            {hasPermission('references', 'update') && (
                                                <button
                                                    className="btn-edit"
                                                    onClick={() => handleEdit(reference)}
                                                >
                                                    ✏️ Modifier
                                                </button>
                                            )}
                                            {hasPermission('references', 'delete') && (
                                                <button
                                                    className="btn-delete"
                                                    onClick={() => handleDelete(reference._id, reference.nom)}
                                                >
                                                    🗑️ Supprimer
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReferencesManagement;
